// CREATE-object NAME slot recognizer (`ddl-create-object-name`) — the slot
// where the NEW object's (possibly schema-qualified) name goes, directly after
// a `CREATE … <object-type>` head. Split out of `editorContextSlot.ddl2.ts`
// along the same seam as the sibling trigger file (review Q3: keep the family
// file inside the ~200-line budget). G11: pure token scan over the tokenised
// statement slice, NO web-tree-sitter.
//
// Shapes that fire (`▮` = caret):
//   CREATE [OR ALTER | OR REPLACE] [MATERIALIZED] VIEW ▮
//   CREATE [TEMP|TEMPORARY|GLOBAL TEMPORARY|UNLOGGED|VIRTUAL] TABLE ▮
//   CREATE TABLE IF NOT EXISTS ▮            (the optional existence guard)
//   CREATE [UNIQUE][CLUSTERED|NONCLUSTERED|FULLTEXT|SPATIAL|BITMAP] INDEX ▮
//   CREATE OR REPLACE FUNCTION|PROCEDURE|PROC|PACKAGE [BODY]|TYPE|SYNONYM|
//                     SEQUENCE|TRIGGER ▮
//   … and the same shapes with a PARTIAL name the caret is still attached to
//   (`CREATE TABLE acact_Ord▮`).
//
// Shapes that do NOT fire — the negatives this recognizer exists to keep out:
//   - the caret moved PAST a complete name: `CREATE TABLE t ▮`, `CREATE VIEW v
//     AS ▮`, `CREATE INDEX ix ON ▮` (the last token before the caret is then a
//     whitespace-separated identifier / keyword, not the object-type keyword).
//   - inside the DDL body: `CREATE TABLE t (▮` and anything under an unclosed
//     `(` — that is the column / constraint list, owned by the other DDL kinds.
//   - the caret still ATTACHED to the object-type keyword or a head modifier
//     (`CREATE TABLE▮`, `CREATE MATERIALIZED▮`) — the user is typing that
//     keyword and firing here would hide its own completion. Same `\s+` rule
//     the rest of the DDL family ports.
//   - a dotted continuation (`CREATE VIEW dbo.▮`) — `deriveCompletionContext`
//     classifies that as `dotted-access` upstream, and the token walk refuses
//     it too (only ONE bare partial identifier may sit before the caret).
//   - a non-CREATE head (`ALTER TABLE ▮`, `DROP VIEW ▮`) — those name an
//     EXISTING object, so the resolvers' relation emission is right there.

import type { ClauseToken } from './editorContextClause';
import { findEnclosingLparenIndex, upper } from './editorContextSlotShared';

/**
 * Object types whose CREATE statement takes a schema-qualifiable name. Kept
 * cross-engine on purpose (parity rule): an engine that lacks one of these
 * never produces the shape, so listing it costs nothing and keeps the five
 * engines' behavior identical where they DO share it. SCHEMA / DATABASE /
 * ROLE / USER are deliberately ABSENT — their name is a namespace itself, not
 * a name INSIDE a namespace, so the namespace emission would be wrong there.
 */
const CREATE_OBJECT_TYPES: ReadonlySet<string> = new Set([
  'VIEW', 'TABLE', 'PROCEDURE', 'PROC', 'FUNCTION', 'TRIGGER',
  'SEQUENCE', 'INDEX', 'TYPE', 'SYNONYM', 'PACKAGE',
]);

/**
 * Keywords legal BETWEEN `CREATE` and the object-type keyword across the five
 * dialects: T-SQL `OR ALTER` / `UNIQUE CLUSTERED` / `COLUMNSTORE`, PG/MySQL
 * `OR REPLACE` / `TEMP[ORARY]` / `UNLOGGED` / `RECURSIVE` / `MATERIALIZED` /
 * `FULLTEXT` / `SPATIAL`, Oracle `GLOBAL|PRIVATE TEMPORARY` / `BITMAP` /
 * `FORCE` / `EDITIONABLE`, SQLite `VIRTUAL` / `TEMP`, PG `CONSTRAINT TRIGGER`.
 */
const CREATE_HEAD_MODIFIERS: ReadonlySet<string> = new Set([
  'OR', 'ALTER', 'REPLACE', 'MATERIALIZED', 'RECURSIVE',
  'TEMP', 'TEMPORARY', 'GLOBAL', 'LOCAL', 'PRIVATE', 'UNLOGGED', 'VIRTUAL',
  'UNIQUE', 'CLUSTERED', 'NONCLUSTERED', 'COLUMNSTORE',
  'FULLTEXT', 'SPATIAL', 'BITMAP', 'CONSTRAINT',
  'FORCE', 'NOFORCE', 'EDITIONABLE', 'NONEDITIONABLE',
]);

/** True for a bare-word token — an object-type keyword may lex as either
 *  `keyword` or `identifier` depending on the dialect's reserved-word list. */
function isWordToken(t: ClauseToken | undefined): boolean {
  return t !== undefined && (t.kind === 'identifier' || t.kind === 'keyword');
}

/**
 * True when the cursor sits at the NEW object's name slot of a CREATE
 * statement — nothing but whitespace, an optional `IF NOT EXISTS`, and at most
 * one partial identifier between the object-type keyword and the caret.
 * `cursorOffset` separates an ATTACHED partial (`CREATE TABLE ord▮`, fires —
 * the name is still being typed) from a COMPLETE name the caret has moved past
 * (`CREATE TABLE ord ▮`, does not fire).
 */
export function createObjectNameSlot(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): boolean {
  if (tokens.length === 0) return false;
  // Never inside a body: `CREATE TABLE t (▮` is the column list, not the name.
  // (The `classifyDdlSlot` caller already returns before reaching here inside
  // an unclosed `(`; mssql's dispatcher does not, so the guard lives here.)
  if (findEnclosingLparenIndex(tokens) >= 0) return false;

  let i = tokens.length - 1;
  // Optional partial name the caret is still ATTACHED to. Exactly one bare
  // word — a dot before it means `schema.▮` dotted access, which is not this
  // slot.
  if (isWordToken(tokens[i]) && tokens[i]!.end >= cursorOffset) i -= 1;
  // Optional `IF NOT EXISTS` guard between the object-type keyword and the name.
  if (
    upper(tokens[i]) === 'EXISTS'
    && upper(tokens[i - 1]) === 'NOT'
    && upper(tokens[i - 2]) === 'IF'
  ) {
    i -= 3;
  }
  // Oracle's two-word object types (`PACKAGE BODY ▮`, `TYPE BODY ▮`).
  if (upper(tokens[i]) === 'BODY') {
    const before = upper(tokens[i - 1]);
    if (before !== 'PACKAGE' && before !== 'TYPE') return false;
    i -= 1;
  }
  const objectType = upper(tokens[i]);
  if (objectType === null || !CREATE_OBJECT_TYPES.has(objectType)) return false;

  // Head must be `CREATE [<modifier>…]` immediately before the object type.
  for (let j = i - 1; j >= 0; j -= 1) {
    const u = upper(tokens[j]);
    // `SHOW CREATE TABLE ▮` (MySQL) READS an existing object's DDL — the
    // caret names a table that must already exist, so the namespace-only
    // emission this slot triggers is exactly wrong there (mysql coverage
    // script.36 §6 wants the relation list and got the database list).
    // `SHOW CREATE …` is the only statement that embeds a CREATE head it does
    // not perform, so one look at the token before `CREATE` settles it.
    if (u === 'CREATE') return upper(tokens[j - 1]) !== 'SHOW';
    if (u === null || !CREATE_HEAD_MODIFIERS.has(u)) return false;
  }
  return false;
}
